import {
  addDays,
  addHours,
  differenceInHours,
  endOfDay,
  endOfMonth,
  endOfWeek,
  formatDate,
  hoursToMilliseconds,
  set,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subHours,
} from 'date-fns';

import {
  ShopOpeningHourOverridesModel,
  ShopOpeningHoursModel,
} from '@merchant-clients/shop';

import { PeriodModel } from '../order-filter-input.interface';

interface Intermediary {
  from: Date;
  to: Date;
}

export const orderFilterBasicPeriodMapper = (
  period: PeriodModel,
  currentTime = new Date(),
): Intermediary => {
  switch (period) {
    case 'TODAY':
      return {
        from: subHours(startOfDay(currentTime), 3),
        to: addHours(endOfDay(currentTime), 3),
      };
    case 'THIS_WEEK':
      return {
        from: subHours(startOfWeek(currentTime, { weekStartsOn: 1 }), 3),
        to: addHours(endOfWeek(currentTime, { weekStartsOn: 1 }), 3),
      };
    case 'THIS_MONTH':
      return {
        from: subHours(startOfMonth(currentTime), 3),
        to: addHours(endOfMonth(currentTime), 3),
      };
  }
};

export const orderFilterPeriodMapper = (
  openingHours: ShopOpeningHoursModel | undefined,
  openingHourOverrides: ShopOpeningHourOverridesModel,
  period: PeriodModel,
  currentTime = new Date(),
): Intermediary => {
  if (!openingHours) {
    let result = mapAlwaysOpenShop(period, currentTime);
    result = applyOverridesIfAvailable(
      result,
      openingHourOverrides,
      currentTime,
    );

    return {
      from: result.from,
      to: result.to,
    };
  }

  return {
    from: startOfDay(currentTime),
    to: endOfDay(currentTime),
  };
};

const mapAlwaysOpenShop = (
  period: PeriodModel,
  currentTime: Date,
): Intermediary => {
  let from = startOfDay(currentTime);
  const to = endOfDay(currentTime);

  if (isCloseToPreviousDay(currentTime)) {
    from = subHours(from, 1);
  }

  switch (period) {
    case 'TODAY':
      return {
        from: from,
        to: isCloseToNextDay(currentTime) ? addHours(to, 4) : to,
      };
    case 'THIS_WEEK':
      return {
        from: startOfWeek(currentTime, { weekStartsOn: 1 }),
        to: endOfWeek(currentTime, { weekStartsOn: 1 }),
      };
    case 'THIS_MONTH':
      return {
        from: startOfMonth(currentTime),
        to: endOfMonth(currentTime),
      };
  }
};

const applyOverridesIfAvailable = (
  result: Intermediary,
  overrides: ShopOpeningHourOverridesModel,
  currentTime: Date,
): Intermediary => {
  const formattedFrom = formatDate(result.from, 'yyyy-MM-dd');
  const formattedTo = formatDate(result.to, 'yyyy-MM-dd');

  const fromOverride = overrides[formattedFrom] ?? [];
  const toOverride = overrides[formattedTo] ?? [];

  if (fromOverride.length > 0) {
    const availableOverrides = fromOverride
      .map((entry) => {
        return parseTimeStringToDate(result.from, entry.startTime);
      })
      .filter((overrideTime) => overrideTime <= currentTime);

    const from = availableOverrides.at(0)!;

    result = {
      ...result,
      from,
    };
  }

  if (toOverride.length > 0) {
    const availableOverrides = toOverride
      .map((entry) => {
        return parseTimeStringToDate(result.to, entry.endTime);
      })
      .filter((overrideTime) => overrideTime >= currentTime);

    const to = availableOverrides.at(-1)!;

    result = {
      ...result,
      to,
    };
  }

  if (result.to.getHours() === 23 && result.to.getMinutes() === 59) {
    // if the to is at the end of the day, we need to check if there is an override for the next day
    const nextDay = addDays(result.to, 1);
    const formattedNextDay = formatDate(nextDay, 'yyyy-MM-dd');
    const nextDayOverrides = overrides[formattedNextDay] ?? [];

    if (nextDayOverrides.length > 0) {
      const nextDayOverride = nextDayOverrides.at(0);

      // If we have an override that continues from midnight, we extend the to
      // to include it
      if (nextDayOverride!.startTime === '00:00') {
        const newTo = parseTimeStringToDate(nextDay, nextDayOverride!.endTime);

        if (differenceInHours(newTo, result.to) > 4) {
          // Potentially the shop could be open a very long time into the next day
          // so limit this to 04:00 to avoid loading the orders for the entire next day
          // Note that after passing midnight the period will be recalculated and then
          // include the entire next day
          return {
            ...result,
            to: set(nextDay, {
              hours: 4,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            }),
          };
        }

        return {
          ...result,
          to: newTo,
        };
      }
    }
  }

  return result;
};

const isCloseToPreviousDay = (date: Date) => {
  const startOfDate = startOfDay(date);
  const diff = date.getTime() - startOfDate.getTime();

  return diff < hoursToMilliseconds(1);
};

const isCloseToNextDay = (date: Date) => {
  const startOfTomorrow = addDays(startOfDay(date), 1);
  const diff = startOfTomorrow.getTime() - date.getTime();

  return diff < hoursToMilliseconds(1);
};

const parseTimeStringToDate = (day: Date, time: string) => {
  const [hours, minutes] = time.split(':');

  return set(day, {
    hours: Number(hours),
    minutes: Number(minutes),
    seconds: 0,
    milliseconds: 0,
  });
};
